import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import { leerOnboardingEnLaBase } from '../lib/cuestionarioEnLaBase';
import { V2_STUDIO_PATH } from '../lib/userGroups';
import { PantallaConCumbre } from '../components/onboarding/corto/PantallaConCumbre';
import { DIALOGO_DEL_NOMBRE } from '../lib/cuestionarioCorto/dialogo';
import { SETUP_BRAND_NAME_KEY, SETUP_BRAND_NAME_MAX } from '../components/onboarding/accountSetup';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';

/**
 * Cuestionario corto, paso 1: el nombre de la marca
 * (ADR-2026-10-31-onboarding-corto-para-la-prueba). Reemplaza al paso del rol: el onboarding
 * de marca, largo, bajaba la conversión del alta, y el rol lo pregunta ese onboarding cuando
 * la persona empieza a pagar.
 *
 * - Lo pregunta Cumbre (ADR-2026-10-31-el-cuestionario-corto-con-el-diseno-del-de-marca); el
 *   campo aparece cuando termina de hablar.
 * - "Siguiente" queda deshabilitado con el nombre vacío; Enter también avanza.
 * - El nombre se guarda en localStorage y lo usa el alta de la empresa, al terminar el paso 2.
 * - Volver atrás desde el rubro no pierde lo escrito.
 */
export const AccountSetupNombre = () => {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState(() => {
    try {
      return localStorage.getItem(SETUP_BRAND_NAME_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const listo = nombre.trim().length > 0;

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    // Quien ya tiene la cuenta hecha y cae acá (una pestaña vieja, un marcador, el "atrás") no
    // vuelve a hacer el cuestionario: va a la app, o al onboarding de marca si lo debe. Lo
    // decide la base; si no se puede leer, o le falta el cuestionario, se queda.
    // ADR-2026-10-31-onboarding-corto-para-la-prueba.
    void leerOnboardingEnLaBase().then((enLaBase) => {
      if (!active) return;
      if (enLaBase === null) navigate(V2_STUDIO_PATH, { replace: true });
      else if (enLaBase === 'marca') navigate(ONBOARDING_V2.marcaSetup, { replace: true });
    });
    trackEvent('onboarding_corto_paso', { paso: 'nombre', accion: 'visto' });
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleNext = (e?: FormEvent) => {
    e?.preventDefault();
    if (!listo) return;
    try {
      localStorage.setItem(SETUP_BRAND_NAME_KEY, nombre.trim());
    } catch {}
    trackEvent('onboarding_corto_paso', { paso: 'nombre', accion: 'completado' });
    navigate(ONBOARDING_V2.setupCategoria);
  };

  return (
    <PantallaConCumbre tramos={DIALOGO_DEL_NOMBRE}>
      <form id="setup-nombre" onSubmit={handleNext} noValidate className="flex flex-col gap-3">
        <label htmlFor="nombre-de-marca" className="sr-only">
          Nombre de tu marca
        </label>
        <div className="om-campo">
          <div className="om-campo__dentro">
            <span
              className={`material-symbols-rounded text-[20px] transition-colors ${nombre ? 'text-brand-primary' : 'text-[#98A1C0]'}`}
              aria-hidden="true"
            >
              storefront
            </span>
            <input
              id="nombre-de-marca"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={SETUP_BRAND_NAME_MAX}
              autoFocus
              autoComplete="organization"
              placeholder="Ej: Lupe Indumentaria"
              className="min-w-0 flex-1 bg-transparent text-[17px] font-semibold text-brand-black outline-none placeholder:font-normal placeholder:text-[#98A1C0]"
            />
          </div>
        </div>
        <div>
          <button type="submit" disabled={!listo} className="om-boton">
            Siguiente
            <kbd className="hidden rounded bg-white/15 px-1.5 text-[11px] md:inline">⏎</kbd>
          </button>
        </div>
      </form>
    </PantallaConCumbre>
  );
};

export default AccountSetupNombre;
